import { useEffect, useState } from 'react';
import api from '../api/client';
import { useFincaId } from '../context/FincaContext';

export default function ParametrosConfig({ onCerrar, embebido = false }) {
  const fincaIdContext = useFincaId();
  const [config, setConfig] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  useEffect(() => {
    if (!fincaIdContext) return;
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fincaIdContext]);

  const cargar = async () => {
    setCargando(true);
    try {
      const { data } = await api.get('/parametros/config');
      setConfig(data);
    } catch {
      setMensaje({ tipo: 'error', texto: 'Error al cargar configuración' });
    } finally {
      setCargando(false);
    }
  };

  const guardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    setMensaje(null);
    try {
      const payload = {
        oxigeno_critico_max: parseFloat(config.oxigeno_critico_max),
        oxigeno_barbeo_max: parseFloat(config.oxigeno_barbeo_max),
        oxigeno_recuperando_max: parseFloat(config.oxigeno_recuperando_max),
        oxigeno_estable_max: parseFloat(config.oxigeno_estable_max),
        temperatura_min: parseFloat(config.temperatura_min),
        temperatura_max: parseFloat(config.temperatura_max),
      };
      await api.put('/parametros/config', payload);
      setMensaje({ tipo: 'ok', texto: '✅ Configuración actualizada' });
      setTimeout(() => {
        setMensaje(null);
        if (onCerrar) onCerrar();
      }, 1500);
    } catch (err) {
      setMensaje({
        tipo: 'error',
        texto: err.response?.data?.error || 'Error al guardar',
      });
    } finally {
      setGuardando(false);
    }
  };

  const setCampo = (k, v) => setConfig((c) => ({ ...c, [k]: v }));

  if (cargando) {
    return (
      <div className="p-8 text-center text-gray-500">
        Cargando configuración...
      </div>
    );
  }

  const contenido = (
    <form onSubmit={guardar} className="space-y-5">
      {mensaje && (
        <div
          className={`p-3 rounded text-sm ${
            mensaje.tipo === 'ok'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {mensaje.texto}
        </div>
      )}

      {/* ─── Oxígeno ─── */}
      <div className="bg-white rounded border overflow-hidden">
        <div className="px-4 py-2 bg-gradient-to-r from-cyan-50 to-white border-b">
          <h3 className="text-sm font-semibold text-cyan-900">
            💧 Umbrales de oxígeno (mg/L)
          </h3>
        </div>
        <div className="p-4 grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-medium text-red-700 flex items-center gap-1">
              🔴 Crítico (menor a)
            </span>
            <input
              type="number"
              step="0.01"
              value={config.oxigeno_critico_max ?? ''}
              onChange={(e) => setCampo('oxigeno_critico_max', e.target.value)}
              className="mt-1 w-full px-2 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
              required
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-orange-700 flex items-center gap-1">
              🟠 Posible barbeo (menor a)
            </span>
            <input
              type="number"
              step="0.01"
              value={config.oxigeno_barbeo_max ?? ''}
              onChange={(e) => setCampo('oxigeno_barbeo_max', e.target.value)}
              className="mt-1 w-full px-2 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              required
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-amber-700 flex items-center gap-1">
              🟡 Recuperando (menor a)
            </span>
            <input
              type="number"
              step="0.01"
              value={config.oxigeno_recuperando_max ?? ''}
              onChange={(e) => setCampo('oxigeno_recuperando_max', e.target.value)}
              className="mt-1 w-full px-2 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              required
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-green-700 flex items-center gap-1">
              🟢 Estable (menor a)
            </span>
            <input
              type="number"
              step="0.01"
              value={config.oxigeno_estable_max ?? ''}
              onChange={(e) => setCampo('oxigeno_estable_max', e.target.value)}
              className="mt-1 w-full px-2 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
              required
            />
          </label>
        </div>
        <div className="px-4 pb-3 text-xs text-gray-500">
          ✅ Óptimo: ≥ {config.oxigeno_estable_max ?? '—'} mg/L
        </div>
      </div>

      {/* ─── Temperatura ─── */}
      <div className="bg-white rounded border overflow-hidden">
        <div className="px-4 py-2 bg-gradient-to-r from-orange-50 to-white border-b">
          <h3 className="text-sm font-semibold text-orange-900">
            🌡️ Rango de temperatura (°C)
          </h3>
        </div>
        <div className="p-4 grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-medium text-gray-700">Mínimo</span>
            <input
              type="number"
              step="0.01"
              value={config.temperatura_min ?? ''}
              onChange={(e) => setCampo('temperatura_min', e.target.value)}
              className="mt-1 w-full px-2 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              required
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-gray-700">Máximo</span>
            <input
              type="number"
              step="0.01"
              value={config.temperatura_max ?? ''}
              onChange={(e) => setCampo('temperatura_max', e.target.value)}
              className="mt-1 w-full px-2 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              required
            />
          </label>
        </div>
      </div>

      {/* ─── Botones ─── */}
      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={cargar}
          className="px-4 py-2 border rounded hover:bg-gray-100 text-sm"
          disabled={guardando}
        >
          Restaurar
        </button>
        <button
          type="submit"
          className="px-5 py-2 bg-cyan-700 text-white rounded hover:bg-cyan-800 disabled:opacity-50 text-sm font-medium"
          disabled={guardando}
        >
          {guardando ? 'Guardando...' : '💾 Guardar'}
        </button>
      </div>
    </form>
  );

  // Modo embebido (dentro de un modal): solo el formulario
  if (embebido) {
    return contenido;
  }

  // Modo página completa
  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold text-cyan-700 mb-6">
        Configuración de parámetros
      </h1>
      {contenido}
    </div>
  );
}